import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAbility } from '@casl/react';
import { Bell, Info, Loader2, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Combobox } from '../../components/ui/combobox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { PageHeader } from '../../components/common/PageHeader';
import { LoadingPageSkeleton } from '../../components/common/LoadingPageSkeleton';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { learningApi } from '../../utils/learning-api';
import api from '../../utils/api';
import type {
    LearningNotificationEvent,
    LearningNotificationSettings,
    LearningRecipientGroup,
} from '../../types/learning.types';

/**
 * Learning Settings — the notification configuration centre (LEARNING.md §16).
 *
 * Which of the five events mail, the reminder cadence, and any extra people appended to a
 * recipient group. Modelled on Stock's Delivery Settings page.
 *
 * RECIPIENTS ARE NOT CHOSEN HERE. They are resolved from the data by
 * LearningNotificationService — the learner the assignment belongs to, that person's manager,
 * and HR (active users holding a global role). Each row names the group it reaches so the
 * switch is understandable; the pickers below only ADD people on top.
 */

const EVENT_ROWS: {
    key: LearningNotificationEvent;
    label: string;
    trigger: string;
    recipients: string;
    /** Which extra-recipient groups this event actually has, so the row shows the right ones. */
    groups: LearningRecipientGroup[];
}[] = [
    {
        key: 'course_assigned',
        label: 'Course assigned',
        trigger: 'When a course is assigned to a learner',
        recipients: 'The learner, and their manager',
        groups: ['learner', 'manager'],
    },
    {
        key: 'course_reminder',
        label: 'Course reminder',
        trigger: 'At each cadence threshold below, and on the overdue repeat',
        recipients: 'The learner, and their manager',
        groups: ['learner', 'manager'],
    },
    {
        key: 'course_cancelled',
        label: 'Course cancelled',
        trigger: 'When an assignment is removed (Delete, or Remove People)',
        recipients: 'The learner, and their manager',
        groups: ['learner', 'manager'],
    },
    {
        key: 'course_in_progress',
        label: 'Course in progress',
        trigger: 'When a learner opens a course for the first time',
        recipients: 'HR — every active user with a global role',
        groups: ['hr'],
    },
    {
        key: 'course_completed',
        label: 'Course completed',
        trigger: 'When a learner marks a course complete',
        recipients: 'The learner (with certificate), their manager, and HR',
        groups: ['learner', 'manager', 'hr'],
    },
];

const GROUP_LABELS: Record<LearningRecipientGroup, string> = {
    learner: 'Learners',
    manager: 'Managers',
    hr: 'HR',
};

/** Offered as one-tap toggles; any other value can be typed in. */
const CADENCE_PRESETS = [30, 14, 7, 3, 1];

const EMPTY_EVENTS: Record<LearningNotificationEvent, boolean> = {
    course_assigned: true,
    course_reminder: true,
    course_cancelled: true,
    course_in_progress: true,
    course_completed: true,
};

const EMPTY_EXTRAS: Record<LearningRecipientGroup, string[]> = { learner: [], manager: [], hr: [] };

interface PickerOption {
    label: string;
    value: string;
    subtitle?: string;
}

interface AllUser {
    id: number;
    name: string;
    email?: string;
}

/** A labelled entry for an id already saved but no longer offered — an archived user. */
const withSavedFallback = (live: PickerOption[], savedIds: string[]) => {
    const known = new Set(live.map((option) => option.value));
    const extra = [...new Set(savedIds)]
        .filter((id) => !known.has(id))
        .map((id) => ({ value: id, label: `User ${id} (unavailable)` }));
    return [...live, ...extra];
};

export default function LearningSettingsPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.LEARNING_SETTINGS);
    const canUpdate = ability.can(ACTIONS.UPDATE, RESOURCES.LEARNING_SETTINGS);

    const [settings, setSettings] = useState<LearningNotificationSettings | null>(null);
    const [events, setEvents] = useState<Record<LearningNotificationEvent, boolean>>(EMPTY_EVENTS);
    const [cadence, setCadence] = useState<number[]>([]);
    const [overdueRepeat, setOverdueRepeat] = useState('');
    const [extras, setExtras] = useState<Record<LearningRecipientGroup, string[]>>(EMPTY_EXTRAS);
    const [customDays, setCustomDays] = useState('');
    const [userChoices, setUserChoices] = useState<PickerOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const names = useMemo(() => {
        const map = new Map<string, string>();
        userChoices.forEach((option) => map.set(option.value, option.label));
        return map;
    }, [userChoices]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [loaded, usersResp] = await Promise.all([
                learningApi.settings.get(),
                // Tolerant: the switches still render if the user picker's list fails, the
                // extras would then just show bare ids.
                api.get<{ success: boolean; users: AllUser[] }>('/api/users?all_users=true')
                    .catch(() => ({ success: false, users: [] as AllUser[] })),
            ]);

            setSettings(loaded);
            setEvents({ ...EMPTY_EVENTS, ...loaded.events });
            setCadence(loaded.reminderCadenceDays ?? []);
            setOverdueRepeat(
                loaded.reminderOverdueRepeatDays != null ? String(loaded.reminderOverdueRepeatDays) : '',
            );
            const loadedExtras = loaded.additionalRecipients ?? EMPTY_EXTRAS;
            setExtras({
                learner: loadedExtras.learner ?? [],
                manager: loadedExtras.manager ?? [],
                hr: loadedExtras.hr ?? [],
            });

            const choices: PickerOption[] = (usersResp.users || []).map((user) => ({
                label: user.name,
                value: String(user.id),
                subtitle: user.email,
            }));
            setUserChoices(withSavedFallback(choices, [
                ...(loadedExtras.learner ?? []),
                ...(loadedExtras.manager ?? []),
                ...(loadedExtras.hr ?? []),
            ]));
            setLoadError(null);
        } catch (err) {
            setLoadError(err instanceof Error && err.message
                ? err.message
                : 'Failed to load the notification settings');
        } finally {
            setLoading(false);
            setHasLoaded(true);
        }
    }, []);

    useEffect(() => { if (canRead) load(); }, [canRead, load]);

    const save = async () => {
        try {
            setSaving(true);
            setSaveError(null);
            const repeatDays = Number(overdueRepeat);
            await learningApi.settings.update({
                events,
                reminderCadenceDays: cadence,
                reminderOverdueRepeatDays: overdueRepeat.trim() !== '' && Number.isFinite(repeatDays) && repeatDays > 0
                    ? repeatDays
                    : null,
                additionalRecipients: extras,
            });
            toast.success('Learning notification settings saved');
            await load();
        } catch (err) {
            setSaveError(err instanceof Error && err.message
                ? err.message
                : 'Could not save the notification settings');
        } finally {
            setSaving(false);
        }
    };

    const addCadenceDays = (days: number) => {
        if (!Number.isInteger(days) || days <= 0) return;
        setCadence((previous) => [...new Set([...previous, days])].sort((a, b) => b - a));
        setSaveError(null);
    };

    const renderEventRow = (row: (typeof EVENT_ROWS)[number]) => {
        const enabled = events[row.key] !== false;
        return (
            <div key={row.key} className="flex items-start justify-between gap-4 border-b border-border py-4 last:border-b-0">
                <div className="min-w-0">
                    <div className="text-sm font-medium">{row.label}</div>
                    <p className="mt-0.5 text-xs text-muted-foreground">{row.trigger}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground/80">To:</span> {row.recipients}
                    </p>
                </div>
                <Switch
                    checked={enabled}
                    disabled={!canUpdate}
                    aria-label={`${row.label} notifications`}
                    onCheckedChange={(checked) => {
                        setEvents((previous) => ({ ...previous, [row.key]: checked }));
                        setSaveError(null);
                    }}
                />
            </div>
        );
    };

    const renderExtras = (group: LearningRecipientGroup) => {
        const values = extras[group];
        return (
            <div key={group} className="mt-4">
                <h3 className="text-sm font-medium">{GROUP_LABELS[group]} — extra recipients</h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                    Added on top of the people above. Nobody added here replaces the automatic recipients.
                </p>

                {values.length > 0 && (
                    <ul className="mt-3 divide-y divide-border rounded-md border border-border">
                        {values.map((id) => (
                            <li key={id} className="flex items-center justify-between gap-3 px-3 py-2">
                                <span className="min-w-0 truncate text-sm">
                                    {names.get(id) ?? `User ${id}`}
                                </span>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    disabled={!canUpdate}
                                    aria-label={`Remove ${names.get(id) ?? `user ${id}`}`}
                                    onClick={() => {
                                        setExtras((previous) => ({
                                            ...previous,
                                            [group]: previous[group].filter((entry) => entry !== id),
                                        }));
                                        setSaveError(null);
                                    }}
                                >
                                    <Trash2 size={16} />
                                </Button>
                            </li>
                        ))}
                    </ul>
                )}

                {canUpdate && (
                    <div className="mt-3">
                        <Combobox
                            options={userChoices.filter((option) => !values.includes(option.value))}
                            value={undefined}
                            onChange={(value) => {
                                if (!value || values.includes(value)) return;
                                setExtras((previous) => ({ ...previous, [group]: [...previous[group], value] }));
                                setSaveError(null);
                            }}
                            placeholder="Add a person"
                            searchPlaceholder="Search people…"
                            emptyMessage="No user matches that search."
                            className="w-full md:w-80"
                        />
                    </div>
                )}
            </div>
        );
    };

    if (!canRead) return <AccessRestricted />;
    if (loading && !hasLoaded) return <LoadingPageSkeleton />;

    if (loadError && !settings) {
        return (
            <div className="space-y-4 p-2 md:p-0">
                <PageHeader title="Learning Settings" description="Notification configuration." />
                <Card>
                    <CardContent className="py-10 text-center">
                        <p className="text-sm text-destructive">{loadError}</p>
                        <Button variant="outline" className="mt-4" onClick={load}>Try again</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const reminderEnabled = events.course_reminder !== false;

    return (
        <div className="space-y-4 p-2 md:space-y-6 md:p-0">
            <PageHeader
                title="Learning Settings"
                description="Choose which Learning emails go out, and to whom."
                actions={
                    canUpdate ? (
                        <Button className="w-full md:w-auto" onClick={save} disabled={saving}>
                            {saving && <Loader2 size={16} className="animate-spin" />}
                            Save changes
                        </Button>
                    ) : undefined
                }
            />

            <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/30 px-4 py-3">
                <Info size={16} className="mt-0.5 flex-shrink-0 text-muted-foreground" />
                <p className="text-xs leading-relaxed text-muted-foreground">
                    Recipients are worked out from the data — the learner an assignment belongs to,
                    their manager, and HR. Use the extra-recipient lists to add named people on top;
                    nothing here replaces the automatic recipients.
                </p>
            </div>

            {saveError && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                    {saveError}
                </div>
            )}

            <Card>
                <CardContent className="p-4 md:p-6">
                    <div className="flex items-center gap-2">
                        <Bell size={18} />
                        <h2 className="text-base font-semibold">Notifications</h2>
                    </div>
                    <div className="mt-3">
                        {EVENT_ROWS.map(renderEventRow)}
                    </div>
                </CardContent>
            </Card>

            <Card className={reminderEnabled ? undefined : 'opacity-60'}>
                <CardContent className="p-4 md:p-6">
                    <div className="flex items-center gap-2">
                        <h2 className="text-base font-semibold">Reminder cadence</h2>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                        How many days before the due date the reminder goes out. Each threshold is
                        sent once per assignment.
                    </p>

                    <div className="mt-4 space-y-1.5">
                        <Label>Days before the due date</Label>
                        <div className="flex flex-wrap items-center gap-2">
                            {cadence.length === 0 && (
                                <span className="text-xs text-muted-foreground">
                                    No reminders will be sent.
                                </span>
                            )}
                            {cadence.map((days) => (
                                <span
                                    key={days}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 py-1 text-sm"
                                >
                                    {days} day{days === 1 ? '' : 's'}
                                    {canUpdate && (
                                        <button
                                            type="button"
                                            aria-label={`Remove ${days} day reminder`}
                                            className="text-muted-foreground hover:text-foreground"
                                            onClick={() => {
                                                setCadence((previous) => previous.filter((entry) => entry !== days));
                                                setSaveError(null);
                                            }}
                                        >
                                            <X size={12} />
                                        </button>
                                    )}
                                </span>
                            ))}
                        </div>

                        {canUpdate && (
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                {CADENCE_PRESETS.filter((days) => !cadence.includes(days)).map((days) => (
                                    <Button
                                        key={days}
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="rounded-full"
                                        onClick={() => addCadenceDays(days)}
                                    >
                                        + {days}d
                                    </Button>
                                ))}
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="number"
                                        min={1}
                                        max={365}
                                        value={customDays}
                                        onChange={(event) => setCustomDays(event.target.value)}
                                        placeholder="e.g. 21"
                                        className="w-28"
                                        aria-label="Custom days before the due date"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            addCadenceDays(Number(customDays));
                                            setCustomDays('');
                                        }}
                                    >
                                        <Plus size={14} /> Add
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mt-5 w-full max-w-[260px] space-y-1.5">
                        <Label htmlFor="learning-overdue-repeat">
                            Overdue repeat (days, blank = no repeats)
                        </Label>
                        <Input
                            id="learning-overdue-repeat"
                            type="number"
                            min={1}
                            max={365}
                            value={overdueRepeat}
                            disabled={!canUpdate}
                            onChange={(event) => {
                                setOverdueRepeat(event.target.value);
                                setSaveError(null);
                            }}
                            placeholder="e.g. 2"
                        />
                        <p className="text-xs text-muted-foreground">
                            Once the due date has passed, chase every N days until the course is completed.
                        </p>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-4 md:p-6">
                    <h2 className="text-base font-semibold">Extra recipients</h2>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Appended to a group for every event that group belongs to.
                    </p>
                    {(['learner', 'manager', 'hr'] as LearningRecipientGroup[]).map(renderExtras)}
                </CardContent>
            </Card>
        </div>
    );
}
